// API methods
import { getFolio, getLocationExternal } from '@/api/customer'
import { getSnapToRoad } from '@/api/googleMaps/GoogleSnapToRoad'

// Utils
import { latLng } from 'leaflet'
import { mapGetters } from 'vuex'
import { gmapApi } from 'vue2-google-maps'
import geometryUtil from 'leaflet-geometryutil'
import { object, completed, ACCEPTED_STATUS } from '@/utils/status'
import { sendNotification, customerName } from '@/utils/functions'
import { LIGHT } from '@/utils/mapStyles'
import GoogleDirectionsCalculator from '@/factories/directionsCalculator/GoogleDirectionsCalculator'
import Location from '@/domain/Location'
import { getDistanceFrom } from '@/utils/distanceCoordinates'
import lottie from 'lottie-web'

// UI components
import { Message } from 'element-ui'
import NavBar from '@/components/NavBar'
import Map from '@/components/Map'
import TaskCardSteps from '@/components/TaskCardSteps'

// Mixins
import notificationsMethods from '@/utils/mixins/notificationsMethods'

// Lotties data
import driverOnsiteData from '@/assets/lotties/driver_onsite.json'
import completedData from '@/assets/lotties/completed.json'
import cancelledData from '@/assets/lotties/cancelled.json'

// Lotties task type
import inProcessTask from '@/assets/lotties/in_process.json'
import fastfoodTaskType from '@/assets/lotties/fastfood.json'
import internetTaskType from '@/assets/lotties/internet.json'
import labTaskType from '@/assets/lotties/labs.json'
import marketTaskType from '@/assets/lotties/market.json'
import parcelTaskType from '@/assets/lotties/parcel.json'
import pharmacyTaskType from '@/assets/lotties/pharmacy.json'
import telcoTaskType from '@/assets/lotties/telco.json'
import meatTaskType from '@/assets/lotties/meat.json'
import foodTaskType from '@/assets/lotties/food.json'

// Constants
import CONSTANTS from '@/utils/const'

// Local constants
const ANIM_INTERVAL_MAP = 16
const ANIM_INTERVAL = 6000
const ANIM_STEPS_LIMIT = 5

const IS_SNAP_ROAD_API = JSON.parse(process.env.VUE_APP_SNAP_ROAD_API)
const IS_DIRECTIONS_API = JSON.parse(process.env.VUE_APP_DIRECTIONS_API)

const ROUTES_RANGES_DEFAULT = {
  SNAP_ROAD: {
    MIN_DISTANCE: 90,
    MAX_DISTANCE: 300
  },
  DIRECTIONS: {
    MIN_DISTANCE: 301,
    MAX_DISTANCE: 600
  }
}

const FORWARD_CLOSED_ARROW = {
  icon: {
    path: 1,
    scale: 3
  },
  offset: '100%',
  repeat: '100px'
}

const POLYLINE_SETTINGS = {
  SNAP_TO_ROADS: {
    strokeColor: '#009a00',
    strokeWeight: 3,
    icons: [FORWARD_CLOSED_ARROW]
  },
  GPS: {
    strokeColor: '#f54242',
    strokeWeight: 6,
    icons: [FORWARD_CLOSED_ARROW]
  }
}

const ROUTES_RANGES = process.env.VUE_APP_TRACKER_SETTINGS ? JSON.parse(process.env.VUE_APP_TRACKER_SETTINGS) : ROUTES_RANGES_DEFAULT

const ROUTE_ORIGIN = {
  GPS: 'GPS',
  SNAP_ROAD: 'SNAP_ROAD',
  DIRECTIONS: 'DIRECTIONS'
}

function capitalizeFirstLetter(string) {
  let name
  if (string.length > 0) {
    name = string.toLowerCase()
    name = name.charAt(0).toUpperCase() + name.slice(1)
  }
  return name
}

function isValidLocation(location) {
  return (Array.isArray(location) && location.length > 1)
}

const mixin = {
  components: {
    NavBar,
    Map,
    TaskCardSteps
  },
  mixins: [notificationsMethods],
  data() {
    return {
      name: '',
      folio: '',
      admin: '',
      map: {
        componentKey: 0,
        center: JSON.parse(process.env.VUE_APP_COORDS),
        driver: null,
        branch: null,
        delivery: null
      },
      estimatedTime: null,
      isLastLocation: false,
      lastLocation: [],
      dataLocation: [],
      animationDisconnected: false,
      animationStyle: {
        width: '100%',
        height: 'auto',
        overflow: 'hidden',
        margin: '9vh auto 9vh auto'
      },
      animationContainer: null,
      POLYLINE_SETTINGS,
      payload: '',
      snapRoadPath: [],
      zoom: 10,
      bounds: Array(2),
      gmap: undefined,
      disconnectedRouteCoordinates: [],
      googleMapsInitialized: false,
      driver: {
        show: true,
        coords: {},
        bearing: 0,
        path: []
      },
      delivery: {
        show: true,
        coords: {}
      },
      branch: {
        show: true,
        coords: {}
      },
      mapOptions: {
        zoomControl: false,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        disableDefaultUi: false,
        maxZoom: 19,
        styles: LIGHT
      },
      flags: {
        autoCenter: true,
        program: true,
        load: true,
        trackerDevMode: false,
        isTracker: false,
        loading: true,
        isStatusDialog: false
      },
      padding: {
        top: 70,
        left: 20,
        right: 20,
        bottom: 200
      },
      distance: 200,
      socket: {},
      mapLoopAnimationId: undefined,
      mapLoopIntervalId: undefined,
      previousAngle: 0,
      socketURI: process.env.VUE_APP_SOCKET_API,
      constants: {
        centerZoom: 16
      },
      messagesAlerts: [],
      transit: object.TRANSIT
    }
  },
  computed: {
    ...mapGetters(['order', 'statusOrder', 'token']),
    google: gmapApi,
    isOnSiteAnimation() {
      return (this.order.customIndicators && this.order.customIndicators.onSiteAnimation) && !this.flags.loading
    },
    deliveryIcon: function() {
      return {
        url: '/img/svg/ic_marker_delivery.svg',
        scaledSize: new this.google.maps.Size(50, 50),
        anchor: new this.google.maps.Point(25, 25),
        zIndex: 2
      }
    },
    taskTypeAnimation() {
      let _animation = inProcessTask
      switch (this.$route.query.task_type) {
        case CONSTANTS.TASK_TYPE.FASTFOOD:
          _animation = fastfoodTaskType
          break
        case CONSTANTS.TASK_TYPE.PHARMACY:
          _animation = pharmacyTaskType
          break
        case CONSTANTS.TASK_TYPE.PARCEL:
          _animation = parcelTaskType
          break
        case CONSTANTS.TASK_TYPE.MARKET:
          _animation = marketTaskType
          break
        case CONSTANTS.TASK_TYPE.INTERNET:
          _animation = internetTaskType
          break
        case CONSTANTS.TASK_TYPE.LABS:
          _animation = labTaskType
          break
        case CONSTANTS.TASK_TYPE.TELCO:
          _animation = telcoTaskType
          break
        case CONSTANTS.TASK_TYPE.FOOD:
          _animation = foodTaskType
          break
        case CONSTANTS.TASK_TYPE.MEAT:
          _animation = meatTaskType
          break
        default:
          _animation = inProcessTask
          break
      }
      return _animation
    },
    isCash: function() {
      let _isCash = false
      const _notShowInCompletedStatus = !completed.includes(this.order.orderStep)
      if (this.order.paymentMethod === CONSTANTS.CASH && _notShowInCompletedStatus) {
        _isCash = true
      }
      return _isCash
    }
  },
  watch: {
    'order.destiny': function(val) {
      this.delivery.coords = { lat: val.latitude, lng: val.longitude }
    },
    'order.build': function(val) {
      if (this.order.build && this.order.build.coords) {
        this.branch.coords = { lat: val.latitude, lng: val.longitude }
      }
    }
  },
  beforeMount() {
    // Initial coordinates
    this.driver.coords = {}
    if (this.order && this.order.deliveryMan && this.order.deliveryMan.latitude && this.order.deliveryMan.longitude) {
      this.driver.coords = { lat: this.order.deliveryMan.latitude, lng: this.order.deliveryMan.longitude }
    }

    this.driver.bearing = this.order.deliveryMan.bearing
    this.delivery.coords = { lat: this.order.destiny.latitude, lng: this.order.destiny.longitude }
    this.branch.coords = { lat: this.order.build.latitude, lng: this.order.build.longitude }
    this.flags.loading = true
  },
  async mounted() {
    // Notifications
    document.addEventListener('keyup', this.devModeShortCut, false)
    let interval = null
    this.onInitializeMapConnections()
    if (this.order && this.order.externalWorkspace && this.order.orderStep === object.TRANSIT && this.order.folio) {
      const _external = await getLocationExternal(this.order.folio)
      this.onUpdateDriverLocation(_external)
      interval = setInterval(async() => {
        const _external = await getLocationExternal(this.order.folio)
        this.onUpdateDriverLocation(_external)
      }, 10000)
    } else {
      clearInterval(interval)
    }
  },
  beforeDestroy() {
    this.onCloseMapConnections()
  },
  methods: {
    validateLatLng: function(coords) {
      let _isValid = false
      if (coords.lat && coords.lng) {
        _isValid = true
      }
      return _isValid
    },
    onInitializeMapConnections: function() {
      this.connectSocket()
      this.configureSocket()
      this.determineStatus(this.order)
    },
    onCloseMapConnections: function() {
      this.disconnectSocket()
      this.clearIntervals()
      this.clearAnimationFrame()
    },
    visibilityChange: async function(evt, isTabHidden) {
      if (!isTabHidden) {
        const data = {
          folio: this.order.folio,
          ws: ''
        }
        await this.$store.dispatch('GetAuthTokenByFolio', data).then(() => {
        }).catch((err) => {
          if (process.env.NODE_ENV === 'development') {
            console.log(err)
          }
        })
        const folio = await getFolio(this.order.folio)
        if (folio) {
          this.$store.commit('FOLIO', folio)
          this.determineStatus(this.order)
        }
      }
    },
    getOrderAddreess: function(order) {
      return (order.destiny && order.destiny.address) ? order.destiny.address : ''
    },
    onViewFolioStatus: function() {
      this.flags.isStatusDialog = true
    },
    loadMap: function() {
      this.$refs.map.$mapPromise.then((map) => {
        this.gmap = map
        this.googleMapsInitialized = true
        this.generateBounds()

        // Distance
        this.onChangeDriverLocation()
        if (this.order && Array.isArray(this.order.ETAAlerts) && this.order.ETAAlerts.length > 0) {
          this.messagesAlerts = this.order.ETAAlerts
        }
        if ('requestAnimationFrame' in window) {
          this.mapLoopAnimationId = window.requestAnimationFrame(this.mapLoop)
        } else {
          this.mapLoopIntervalId = setInterval(this.mapLoop, ANIM_INTERVAL_MAP)
        }
      })
    },
    initLocationListener() {
      this.googleMapsInitialized = true
      // Distance
      this.onChangeDriverLocation()
      if (this.order && Array.isArray(this.order.ETAAlerts) && this.order.ETAAlerts.length > 0) {
        this.messagesAlerts = this.order.ETAAlerts
      }
    },
    determineStatus: function(order) {
      lottie.destroy()
      let isDisabledDetailButton = false
      let animationData = this.taskTypeAnimation
      let statusImage = '/img/status/status_pending.png'
      let statusLevel = 1
      let statusMessage = this.$t('steps.PENDING.title', { name: customerName(this.order) })
      let statusMessageBody = this.$t('steps.PENDING.description')
      let onSiteAnimation = false
      let isETAAlerts = true
      let driverName = this.$t('general.yourDriver')
      this.flags.loading = true
      this.flags.isTracker = false
      this.animationStyle.width = '70%'
      switch (order.orderStep) {
        case ACCEPTED_STATUS.find(val => val === order.orderStep):
          this.flags.isTracker = false
          lottie.destroy()
          this.animationStyle.width = '70%'
          animationData = this.taskTypeAnimation
          statusImage = '/img/status/status_pending.png'
          statusMessage = this.$t('steps.PENDING.title', { name: customerName(this.order) })
          statusMessageBody = this.$t('steps.PENDING.description')
          statusLevel = 1
          break
        case object.TRANSIT:
          statusImage = '/img/status/pending.png'
          if (order && order.thirdPartyData && order.thirdPartyData.driver && order.thirdPartyData.driver.name) {
            driverName = order.thirdPartyData.driver.name.toUpperCase()
          } else if (order.deliveryMan && order.deliveryMan.name) {
            driverName = capitalizeFirstLetter(order.deliveryMan.name)
          }
          statusMessage = this.$t('steps.TRANSIT.title', { name: driverName })
          statusMessage = this.$t('steps.TRANSIT.description', { name: driverName })
          statusLevel = 2
          this.onEnableTracker()
          break
        case object.COMPLETED_REQUEST:
        case object.ONSITE:
          this.animationStyle.width = '70%'
          this.animationStyle.margin = '9vh auto 9vh auto'
          animationData = driverOnsiteData
          if (order && order.thirdPartyData && order.thirdPartyData.driver && order.thirdPartyData.driver.name) {
            driverName = order.thirdPartyData.driver.name.toUpperCase()
          } else if (order.deliveryMan && order.deliveryMan.name) {
            driverName = capitalizeFirstLetter(order.deliveryMan.name)
          }
          statusMessage = this.$t('steps.ONSITE.title')
          statusMessageBody = this.$t('steps.ONSITE.description', { name: driverName })
          statusLevel = 3
          onSiteAnimation = true
          this.flags.isTracker = false
          break
        case object.COMPLETED:
          lottie.destroy()
          this.animationStyle.width = '70%'
          animationData = completedData
          statusImage = '/img/status/status_completed.png'
          statusMessage = this.$t('steps.COMPLETED.title')
          statusMessageBody = this.$t('steps.COMPLETED.description')
          statusLevel = 4
          isETAAlerts = false
          this.flags.isTracker = false
          break
        case object.CANCELED:
          lottie.destroy()
          this.animationStyle.width = '70%'
          animationData = cancelledData
          statusImage = '/img/status/status_cancel.png'
          statusMessage = this.$t('steps.CANCELED.title')
          statusMessageBody = this.$t('steps.CANCELED.description')
          statusLevel = 4
          isETAAlerts = false
          this.flags.isTracker = false
          isDisabledDetailButton = true
          break
        default:
          lottie.destroy()
          isDisabledDetailButton = false
          animationData = this.taskTypeAnimation
          statusImage = '/img/status/status_pending.png'
          statusLevel = 1
          statusMessage = this.$t('steps.PENDING.title', { name: customerName(this.order) })
          statusMessageBody = this.$t('steps.PENDING.description')
          onSiteAnimation = false
          isETAAlerts = true
          driverName = this.$t('general.yourDriver')
          this.flags.loading = true
          this.animationStyle.width = '70%'
          break
      }
      this.loadAnimation(animationData)
      this.order.customIndicators = {
        animationData,
        statusImage,
        statusLevel,
        statusMessage,
        statusMessageBody,
        isETAAlerts,
        onSiteAnimation,
        isDisabledDetailButton
      }
      this.flags.loading = false
    },
    loadAnimation: function(animationData) {
      this.animationContainer = lottie.loadAnimation({
        container: this.$refs.lavContainer,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: animationData
      })
    },
    onEnableTracker: function() {
      this.flags.isTracker = true
      this.initLocationListener()
    },
    devModeShortCut: function(e) {
      // this would test for D key is 68 and the ctrl key at the same time
      if (e.ctrlKey && e.keyCode === 68) {
        this.flags.trackerDevMode = !this.flags.trackerDevMode
        Message({
          message: 'TRACKER_DEV_MODE_CHANGED',
          type: 'warning',
          duration: 5 * 1000
        })
      }
    },
    clearAnimationFrame: function() {
      if (this.mapLoopAnimationId) {
        window.cancelAnimationFrame(this.mapLoopAnimationId)
      }
    },
    clearIntervals: function() {
      if (this.mapLoopIntervalId) {
        clearInterval(this.mapLoopIntervalId)
      }
      if (this.driver.animation) {
        clearInterval(this.driver.animation)
      }
    },
    mapLoop: async function() {
      if (this.driver.path.length > 0 && typeof this.driver.animation === 'undefined') {
        const driverCoord = this.driver.path[0]

        await this.addDirectionsRoute(driverCoord)

        this.slideTo(this.driver.path[0])
      }
      if ('requestAnimationFrame' in window) {
        window.requestAnimationFrame(this.mapLoop)
      }
    },
    getLocationFromSnapRoad: async function(driverCoords) {
      const snappedPoints = []
      const pathToBeSnapped = `${this.driver.coords.lat},${this.driver.coords.lng}|${driverCoords.lat},${driverCoords.lng}`
      if (this.driver.coords && driverCoords) {
        const response = await getSnapToRoad(pathToBeSnapped)
        if (response) {
          for (let indexPoint = 0; indexPoint < response.length; indexPoint++) {
            snappedPoints.push({
              lat: response[indexPoint].location.latitude,
              lng: response[indexPoint].location.longitude,
              origin: ROUTE_ORIGIN.SNAP_ROAD
            })
          }
        }
      }
      return snappedPoints
    },
    async addDirectionsRoute(driverCoords) {
      // SNAP_ROAD.MIN_DISTANCE-1 and below --> use GPS
      // between SNAP_ROAD.MIN_DISTANCE and SNAP_ROAD.MAX_DISTANCE --> uses SNAP_ROAD
      // between DIRECTIONS.MIN_DISTANCE and DIRECTIONS.MAX_DISTANCE --> uses DIRECTIONS
      // ROUTES_RANGES.DIRECTIONS.MAX_DISTANCE+1 and above --> use GPS
      let extraLocationPoints = []
      let isExtraPoints = false
      if (IS_SNAP_ROAD_API && !this.validateDriverCoordinates(driverCoords, ROUTES_RANGES.SNAP_ROAD.MIN_DISTANCE, ROUTES_RANGES.SNAP_ROAD.MAX_DISTANCE)) {
        extraLocationPoints = await this.getLocationFromSnapRoad(driverCoords)
        this.snapRoadPath = [...extraLocationPoints]
        isExtraPoints = true
      } else if (IS_DIRECTIONS_API && !this.validateDriverCoordinates(driverCoords, ROUTES_RANGES.DIRECTIONS.MIN_DISTANCE, ROUTES_RANGES.DIRECTIONS.MAX_DISTANCE)) {
        extraLocationPoints = await this.calculateMapDirections({
          origin: new Location({
            latitude: this.driver.coords.lat,
            longitude: this.driver.coords.lng
          }),
          destination: new Location({
            latitude: driverCoords.lat,
            longitude: driverCoords.lng
          })
        })
        isExtraPoints = true
      }
      if (isExtraPoints && extraLocationPoints.length > 0) {
        this.driver.path = [
          ...[this.driver.coords],
          ...extraLocationPoints
        ]
      }
    },
    validateDriverCoordinates(driverCoords, minDistance, maxDistance) {
      const validation = (!this.validateOriginDriverCoordinates(driverCoords) ||
        !this.validateDinstanceOfDriverCoordinates(driverCoords, minDistance, maxDistance) ||
        !this.validateCurrentDriverCoordinates()
      )
      return validation
    },
    validateOriginDriverCoordinates(driverCoords) {
      return driverCoords.origin === 'socket'
    },
    validateDinstanceOfDriverCoordinates(driverCoords, minDistance, maxDistance) {
      let pointA
      let pointB
      if (this.driver.coords && this.driver.coords.lat) {
        pointA = latLng(this.driver.coords.lat, this.driver.coords.lng)
        pointB = latLng(driverCoords.lat, driverCoords.lng)
      }

      if (!pointA || !pointB) {
        return
      }

      const lengthDistance = geometryUtil.length([
        pointA,
        pointB
      ])
      return (lengthDistance >= minDistance) && (lengthDistance <= maxDistance)
    },
    validateCurrentDriverCoordinates() {
      return (
        this.driver.coords &&
        this.driver.coords.lat &&
        this.driver.coords.lng
      )
    },
    isDifferentToCurrentDriverCoordinates(driverCoords) {
      return (
        this.driver.coords.lat !== driverCoords.lat &&
        this.driver.coords.lng !== driverCoords.lng
      )
    },
    async calculateMapDirections(data) {
      try {
        const directionsCalculatorInstance = new GoogleDirectionsCalculator()
        directionsCalculatorInstance.setOrigin(data.origin)
        directionsCalculatorInstance.setDestination(data.destination)

        const mapDirections = await directionsCalculatorInstance.execute()

        return this.mapLocationsOfMapStepDirections(mapDirections.routes)
      } catch (error) {
        console.log(error)
        return []
      }
    },
    mapLocationsOfMapStepDirections(mapSteps) {
      const locations = []

      for (const mapStep of mapSteps) {
        locations.push({
          lat: mapStep.location.latitude,
          lng: mapStep.location.longitude,
          bearing: mapStep.location.bearing,
          origin: ROUTE_ORIGIN.DIRECTIONS
        })
      }

      return locations
    },
    callToDriver: function() {
      if (typeof this.order.deliveryMan.phone === 'undefined') {
        return
      }
      if (this.order.deliveryMan.phone.length >= 10) {
        window.open(`tel:${this.order.deliveryMan.phone}`)
      } else {
        this.$confirm(this.$t('messages.errorToCallToDriver', {
          phone: this.order.deliveryMan.phone
        }), {
          confirmButtonText: this.$t('general.confirm'),
          cancelButtonText: this.$t('general.cancel')
        }).then(async() => {
          window.open(`tel:${this.order.deliveryMan.phone}`)
        }).catch(() => {})
      }
    },
    disconnectSocket() {
      if (this.socket) {
        this.socket.disconnect()
        this.socket.close()
      }
    },
    getFolio: async function() {
      const order = await getFolio(this.order.folio)
      this.onUpdateFolio(order)
    },
    setCenter: function() {
      this.flags.autoCenter = !this.flags.autoCenter
      if (this.flags.autoCenter) {
        this.generateBounds()
      } else {
        if (this.driver.coords && this.driver.coords.lat) {
          this.map.center = {
            lat: this.driver.coords.lat,
            lng: this.driver.coords.lng
          }
          this.gmap.setCenter(this.map.center)
          this.gmap.setZoom(this.constants.centerZoom)
        }
      }
    },
    showDetails: function() {
      this.$parent.ticket = false
      this.$router.push({ path: `/folio/${this.order.folio}/detail`, query: { backActive: true }})
    },
    generateBounds: function() {
      const bounds = new window.google.maps.LatLngBounds()
      if (this.driver && this.delivery) {
        let _driverCoords
        let _deliveryCoords
        if (this.driver.coords && (this.driver.coords.lat && this.driver.coords.lng)) {
          _driverCoords = this.driver.coords
        }
        if (this.delivery.coords && (this.delivery.coords.lat && this.delivery.coords.lng)) {
          _deliveryCoords = this.delivery.coords
          bounds.extend(_deliveryCoords)
          try {
            if (_driverCoords && _driverCoords.lat !== 0) {
              bounds.extend(_driverCoords)
            }
          } catch (error) {
            console.log('mapObject error...', error)
          }
        }
        this.$refs.map.$mapObject.fitBounds(bounds, this.padding)
        this.$refs.map.$mapObject.panToBounds(bounds, this.padding)
      }
    },
    generateBoundsByCoordsInAnimation: function(coords, nextPosition) {
      const bounds = new window.google.maps.LatLngBounds()
      if (coords) {
        for (const coord of coords) {
          bounds.extend(coord)
        }
        if (bounds.contains(nextPosition)) {
          return
        }
        try {
          this.$refs.map.$mapObject.fitBounds(bounds, this.padding)
          this.$refs.map.$mapObject.panToBounds(bounds)
        } catch (error) {
          console.log('$mapObject error', error)
        }
      }
    },
    slideTo: function(nextPosition) {
      const intervalsPerAnimation = (this.getAnimationInterval(this.driver.path.length) / ANIM_INTERVAL_MAP)
      try {
        if (this.order && this.order.deliveryMan && nextPosition) {
          this.order.deliveryMan.bearing = nextPosition.bearing === 0 ? this.bearing(this.driver.coords.lat, this.driver.coords.lng, nextPosition.lat, nextPosition.lng) : nextPosition.bearing
        }
      } catch (error) {
        console.log('DRIVER_SET_BEARING_ERROR', error)
      }
      if (this.driver && this.driver.coords && nextPosition) {
        const deltas = {
          lat: (nextPosition.lat - this.driver.coords.lat) / intervalsPerAnimation,
          lng: (nextPosition.lng - this.driver.coords.lng) / intervalsPerAnimation
        }
        let i = 0
        let interval
        this.driver.animation = setInterval(function() {
          if (i >= intervalsPerAnimation) {
            this.onFinishAnimation()
            this.driver.path.shift()
            this.onChangeDriverLocation()
            interval = this.driver.animation
            this.driver.animation = undefined
            clearInterval(interval)
            // Distance
          }
          this.animateMarker(deltas)
          i++
        }.bind(this), ANIM_INTERVAL_MAP)
      }
    },
    getAnimationInterval(steps) {
      let interval = ANIM_INTERVAL
      if (steps > ANIM_STEPS_LIMIT) {
        interval = ANIM_INTERVAL / steps
      }
      return interval
    },
    onFinishAnimation() {
      if (this.flags.autoCenter) {
        this.generateBounds()
      } else {
        if (this.driver.coords && this.driver.coords.lat) {
          this.map.center = {
            lat: this.driver.coords.lat,
            lng: this.driver.coords.lng
          }

          this.gmap.setCenter(this.map.center)
        }
        this.gmap.setZoom(this.constants.centerZoom)
      }
    },
    animateMarker: function(deltas) {
      let lat = this.driver.coords.lat
      let lng = this.driver.coords.lng
      lat += deltas.lat
      lng += deltas.lng
      this.driver.coords = {
        lat,
        lng
      }
    },
    redirectCustom: function(url, isDisabledDelay) {
      if (url) {
        const delay = isDisabledDelay ? 0 : 2000
        setTimeout(function() {
          if (this.badgeCount) {
            url += `&custom_counter=${this.badgeCount}`
          }
          window.location.replace(url)
        }.bind(this), delay)
      }
    },
    async onUpdateFolio(data) {
      let interval = 0
      if (this.order && this.order.folio) {
        const _order = await getFolio(this.order.folio)
        if (_order && _order.externalWorkspace && _order.orderStep === object.TRANSIT) {
          const _external = await getLocationExternal(this.order.folio)
          this.onUpdateDriverLocation(_external)
          interval = setInterval(async() => {
            const external = await getLocationExternal(_order.folio)
            this.onUpdateDriverLocation(external)
          }, 10000)
        } else {
          clearInterval(interval)
          this.determineStatus(this.order)
        }
        if (data.orderStep !== object.CANCEL_REQUEST) {
          this.order.orderStep = data.orderStep
          this.order.movements = data.movements
          this.order.deliveryMan = data.deliveryMan
          this.flags.loading = true
          this.determineStatus(_order)
        }
      }

      // Update branch
      if (data.branch && data.branch.name) {
        this.order.build.name = data.branch.name
      }
      if (data.branch && data.branch.address && isValidLocation(data.branch.address.location)) {
        this.order.build.latitude = data.branch.address.location[1]
        this.order.build.longitude = data.branch.address.location[0]
      }

      // Update destiny fields
      if (this.order && this.order.destiny && data.destiny) {
        if (this.order.destiny.address && data.destiny.address) {
          this.order.destiny.address = data.destiny.address
        }
        if (isValidLocation(data.destiny.location)) {
          this.order.destiny.latitude = data.destiny.location[1]
          this.order.destiny.longitude = data.destiny.location[0]
        }
      }
    },
    async getDirectionsTo(driverLocation, targetLocation) {
      return driverLocation[0] + ',' + driverLocation[1] + ';' + targetLocation[0] + ',' + targetLocation[1]
    },
    /* Request to bring the coordinates from the point of origin to the destination. */
    async getRoute(newLocation, fromLocation) {
      try {
        this.bearingLocation = fromLocation
        const res = await this.getDirectionsTo(fromLocation, newLocation)
        const routes = res.routes
        if (Array.isArray(res.routes) && res.routes.length > 0) {
          if (routes[0].geometry && routes[0].geometry.coordinates) {
            this.disconnectedRouteCoordinates = res.routes[0].geometry.coordinates
            setTimeout(() => {
              this.disconnectedRouteCoordinates = []
              this.lastLocation = []
            }, 6000)
          }
        }
      } catch (error) {
        console.log('error' + error)
      }
    },
    /* Request to bring the estimated time from the point of origin to the destination. */
    async getEstimatedTime(origin, target) {
      let minutes = 0
      try {
        const res = await this.getDirectionsTo(origin, target)
        const routes = res.routes
        if (Array.isArray(res.routes) && res.routes.length > 0) {
          if (routes[0].duration) {
            minutes = res.routes[0].duration / CONSTANTS.time.MINUTES_TO_SECONDS
          }
        }
        minutes = !isNaN(minutes) ? Math.round(minutes) : 0
      } catch (error) {
        console.log('error' + error)
      }
      return minutes
    },
    async onUpdateDriverLocation(data) {
      if (data && data.location && this.driver.show) {
        // Verify last location distance
        let distance = 0
        if (this.driver && this.driver.location && this.isLastLocation === false) {
          const lastDriverLocation = this.driver.location
          distance = getDistanceFrom(lastDriverLocation[0], lastDriverLocation[1], data.location[0], data.location[1])
          this.isLastLocation = true
        } else {
          let _location = data.location
          if (Array.isArray(this.dataLocation) && this.dataLocation.length) {
            _location = this.dataLocation
          }
          distance = getDistanceFrom(_location[0], _location[1], data.location[0], data.location[1])
        }
        /* If the signal has been disconnected, and there is no animation. */
        if ((this.lastLocation.length > 0 || distance >= 300) && this.disconnectedRouteCoordinates.length === 0) {
          const fromLocation = this.lastLocation.length > 0 ? this.lastLocation : this.dataLocation
          await this.getRoute(data.location, fromLocation)
          /* While in animation. */
        } else if (this.disconnectedRouteCoordinates.length > 0) {
          this.animationDisconnected = true
          /* Every time the driver is updated */
        } else {
          data.location = [data.location[0], data.location[1]]
          this.driver.coords = {
            lat: data.location[1],
            lng: data.location[0],
            bearing: data.bearing,
            origin: 'socket',
            speed: data.speed
          }
          this.animationDisconnected = false
        }
        this.dataLocation = data.location
        // Load ETA
        if (this.estimatedTime === null) {
          let destination = []
          if (this.order && this.order.destiny) {
            destination = [
              this.order.destiny.longitude,
              this.order.destiny.latitude
            ]
          }
          const estimatedMinutes = await this.getEstimatedTime(data.location, destination)
          if (!isNaN(estimatedMinutes) && estimatedMinutes > 0) {
            this.estimatedTime = this.$moment().add(estimatedMinutes, 'minute').format('LT').toLowerCase()
          }
        }
      }
    },
    async onChangeDriverLocation() {
      const pointA = latLng(this.order.deliveryMan.latitude, this.order.deliveryMan.longitude)
      const pointB = latLng(this.order.destiny.latitude, this.order.destiny.longitude)

      if (!pointA || !pointB) {
        return
      }
      // Calculate length between destiny and delivery man position
      const length = geometryUtil.length([
        pointA,
        pointB
      ])
      // if the distance between the two points is 200 and the notification
      // has not yet been sent
      if (length <= this.distance && typeof this.statusOrder[this.order.folio] === 'undefined') {
        this.$store.commit('STATUS_ORDER', this.order.folio)
        sendNotification(this.$t('messages.deliveryManDear'))
      }
      // Update location
      this.driver.coords = { lat: this.order.deliveryMan.latitude, lng: this.order.deliveryMan.longitude }
      this.driver.bearing = this.order.deliveryMan.bearing
    },
    onSocketError(data) {
      if (process.env.NODE_ENV === 'development') {
        console.log('error')
        console.log(data)
      }
      this.disconnectSocket()
    },
    toRadians: function(degrees) {
      return degrees * Math.PI / 180
    },
    toDegrees: function(radians) {
      return radians * 180 / Math.PI
    },
    bearing: function(startLat, startLng, destLat, destLng) {
      startLat = this.toRadians(startLat)
      startLng = this.toRadians(startLng)
      destLat = this.toRadians(destLat)
      destLng = this.toRadians(destLng)

      const y = Math.sin(destLng - startLng) * Math.cos(destLat)
      const x = Math.cos(startLat) * Math.sin(destLat) -
            Math.sin(startLat) * Math.cos(destLat) * Math.cos(destLng - startLng)
      let brng = Math.atan2(y, x)
      brng = this.toDegrees(brng)
      return (brng + 360) % 360
    },
    onClose: function() {
      this.flags.isStatusDialog = false
    }
  }
}

export default mixin
